import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Box, Typography, Grid, CardContent, Button, IconButton, Tooltip } from "@mui/material"
import { IconArrowLeft, IconRefresh } from "@tabler/icons-react"
import { motion, AnimatePresence } from "framer-motion"
import data from "./yanbal-data.json"

interface YanbalData {
  questions: {
    id: string
    text: string
    answers: {
      id: string
      text: string
      image: string
      perfumes: string[]
    }[]
  }[]
  perfumes: {
    name: string
    image: string
  }[]
}

const yanbalData: YanbalData = data as YanbalData

const YanbalCards = () => {
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [showResult, setShowResult] = useState(false)
  const [isFlipped, setIsFlipped] = useState(false)

  const handleSelect = (answerId: string) => {
    const nextSelected = [...selected]
    nextSelected[step] = answerId
    setSelected(nextSelected)
    if (step < 2) {
      setTimeout(() => setStep(step + 1), 500)
    } else {
      setTimeout(() => {
        setIsFlipped(true)
        setTimeout(() => setShowResult(true), 700)
      }, 700)
    }
  }

  const handleRestart = () => {
    setStep(0)
    setSelected([])
    setShowResult(false)
    setIsFlipped(false)
  }

  // Obtener perfumes seleccionados por cada respuesta
  const selectedPerfumes = yanbalData.questions.map((q, idx) => {
    const ansId = selected[idx]
    return q.answers.find(a => a.id === ansId)?.perfumes || []
  })

  // Calcular coincidencias
  const perfumeCounts: Record<string, number> = {}
  selectedPerfumes.forEach(arr => {
    arr.forEach(name => { perfumeCounts[name] = (perfumeCounts[name] || 0) + 1 })
  })
  const perfumes3 = Object.keys(perfumeCounts).filter(k => perfumeCounts[k] === 3)
  const perfumes2 = Object.keys(perfumeCounts).filter(k => perfumeCounts[k] === 2)
  const getPerfume = (name: string) => yanbalData.perfumes.find(p => p.name === name)

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Tooltip title="Volver">
          <IconButton onClick={() => navigate('/tools')} sx={{ mr: 2, color: 'primary.main' }}>
            <span className="material-icons"><IconArrowLeft size={24} /></span>
          </IconButton>
        </Tooltip>
        <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
          Test de Perfumes
        </Typography>
        <Tooltip title="Reiniciar">
          <IconButton onClick={handleRestart}>
            <IconRefresh size={22} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Preguntas y selección de cartas */}
      {!showResult && (
        <Box>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <CardContent
              style={{
                paddingBottom: 10,
              }}
              sx={{
                background: '#fff7f1',
                borderRadius: 3,
                boxShadow: '0 2px 12px 0 #FF6B0033',
                p: 1,
                minWidth: { xs: '90%', sm: 420 },
                maxWidth: 520,
                textAlign: 'center',
                border: '2px solid #FF6B00',
                fontWeight: 600,
                transition: 'box-shadow 0.2s',
              }}>
              <Typography variant="h6" sx={{ color: '#FF6B00', fontWeight: 700 }}>
                {yanbalData.questions[step].text}
              </Typography>
            </CardContent>
          </Box>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.24, ease: 'easeInOut' }}
            >
              <Grid container spacing={2} justifyContent="center">
                {yanbalData.questions[step].answers.map((ans, idx) => (
                  <Grid item xs={6} sm={6} md={3} key={ans.id}>
                    <Box
                      component={motion.div}
                      whileTap={selected[step] === ans.id ? { scale: 1.05 } : {}}
                      transition={{ type: 'spring', stiffness: 340, damping: 24 }}
                      sx={{
                        cursor: 'pointer',
                        border: selected[step] === ans.id ? '2.5px solid #FF6B00' : '2px solid #f0f0f0',
                        boxShadow: selected[step] === ans.id ? '0 4px 16px 0 #FF6B0033' : '0 2px 8px 0 #0001',
                        minHeight: { xs: 120, sm: 160 },
                        aspectRatio: '3/4',
                        borderRadius: 4,
                        background: '#fff',
                        transition: 'box-shadow 0.2s, border 0.2s',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                        py: 3,
                        px: 1.5,
                        my: { xs: 1, sm: 2 },
                      }}
                      onClick={() => handleSelect(ans.id)}
                    >
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 0, width: '100%' }}>
                        <img src={ans.image} alt={ans.text} style={{ width: 72, height: 72, marginBottom: 12, borderRadius: 12, objectFit: 'cover', boxShadow: selected[step] === ans.id ? '0 0 0 3px #FF6B00' : '0 0 0 1px #eee', transition: 'box-shadow 0.18s' }} />
                        <Typography variant="subtitle1" align="center" sx={{ fontWeight: 500, fontSize: { xs: 16, sm: 17 } }}>{ans.text}</Typography>
                      </CardContent>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </AnimatePresence>
          {/* Slider de progreso */}
          <Box sx={{ mt: 1, mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box sx={{ width: 180, maxWidth: '90vw', position: 'relative', height: 24 }}>
              {/* Barra de fondo */}
              <Box sx={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 6, bgcolor: '#ffe1cc', borderRadius: 3, transform: 'translateY(-50%)' }} />
              {/* Barra de progreso animada */}
              <motion.div
                initial={false}
                animate={{ width: `${(step) / 2 * 100}%` }}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  height: 6,
                  background: '#FF6B00',
                  borderRadius: 3,
                  transform: 'translateY(-50%)',
                }}
                transition={{ duration: 0.35, ease: 'easeInOut' }}
              />
              {/* Círculos de pasos */}
              {Array.from({ length: 3 }).map((_, idx) => {
                const isDone = selected[idx]
                const isCurrent = step === idx
                return (
                  <Box
                    key={idx}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: `${(idx / 2) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                      width: 22, height: 22,
                      borderRadius: '50%',
                      bgcolor: isDone ? '#FF6B00' : '#fff',
                      border: isCurrent ? '3px solid #FF6B00' : '2px solid #FF6B00',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: isDone ? '#fff' : '#FF6B00',
                      fontWeight: 700,
                      fontSize: 16,
                      boxShadow: isCurrent ? '0 0 0 4px #ff6b0022' : undefined,
                      transition: 'all 0.22s',
                    }}
                  >
                    {isDone ? (
                      <span style={{ fontSize: 16, fontWeight: 900, lineHeight: 1 }}>✓</span>
                    ) : (
                      idx + 1
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        </Box>
      )}

      {/* Resultado: cartas volteadas y perfumes */}
      {showResult && (
        <Box sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {yanbalData.questions.map((q, idx) => {
              const ans = q.answers.find(a => a.id === selected[idx])
              return (
                <Box key={q.id} sx={{ width: 180, position: 'relative', height: 280, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                  {/* Cara de perfumes */}
                  <Box
                    sx={{
                      position: 'absolute', width: '100%', height: '100%', display: isFlipped ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
                      background: '#fff7f1', borderRadius: 3, boxShadow: 4, border: '2.5px solid #FF6B00', p: 1,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Perfumes</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                      {ans?.perfumes.map(name => {
                        const p = getPerfume(name)
                        return p ? (
                          <Tooltip title={p.name} key={p.name}>
                            <img src={p.image} alt={p.name} style={{ width: 40, height: 40, borderRadius: 6, objectFit: 'cover' }} />
                          </Tooltip>
                        ) : null
                      })}
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>

          <Typography variant="h6" align="center" sx={{ my: 1, color: '#FF6B00', fontWeight: 700 }}>
            Tus perfumes ideales
          </Typography>

          {/* Perfumes con 3 coincidencias */}
          {perfumes3.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" color="primary" align="center">Coincidencia perfecta (3/3):</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                {perfumes3.map(name => {
                  const p = getPerfume(name)
                  return p ? (
                    <Box key={p.name} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img src={p.image} alt={p.name} style={{ width: 56, height: 56, marginBottom: 4, borderRadius: 8, objectFit: 'cover' }} />
                      <Typography variant="caption">{p.name}</Typography>
                    </Box>
                  ) : null
                })}
              </Box>
            </Box>
          )}

          {/* Perfumes con 2 coincidencias */}
          {perfumes2.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="subtitle1" color="secondary" align="center">Otras buenas opciones (2/3):</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                {perfumes2.map(name => {
                  const p = getPerfume(name)
                  return p ? (
                    <Box key={p.name} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <img src={p.image} alt={p.name} style={{ width: 48, height: 48, marginBottom: 4, borderRadius: 8, objectFit: 'cover' }} />
                      <Typography variant="caption">{p.name}</Typography>
                    </Box>
                  ) : null
                })}
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button variant="contained" sx={{ background: '#FF6B00', color: '#fff', fontWeight: 700, '&:hover': { background: '#e55d00' } }} onClick={handleRestart}>
              Volver a intentar
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default YanbalCards